import React, { useState, useEffect } from "react";
import { Save, MapPin, Users, Info, AlignLeft, Building } from "lucide-react";
import FormHeader from "../Common/FormHeader"; 

const FormVenue = ({ onBack, onSave, initialData = null }) => {
  // Definisi State Awal yang Stabil untuk Venue
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    capacity: "",
    status: "Active",
    facilities: "",
    description: "",
    image: null
  });

  // Sinkronisasi data saat Edit Mode
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        name: initialData.name || "",
        location: initialData.location || "",
        capacity: initialData.capacity || "",
        status: initialData.status || "Active",
        facilities: initialData.facilities || "",
        description: initialData.description || "",
        image: initialData.image || null
      });
    } else {
      setFormData({
        name: "", location: "", capacity: "",
        status: "Active", facilities: "",
        description: "", image: null
      });
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="p-6 space-y-6 bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm border border-stone-100 dark:border-white/5 animate-in fade-in duration-500">
      
      {/* HEADER DENGAN TOMBOL BACK */}
      <FormHeader 
        title={initialData ? "Edit Venue" : "Create New Venue"}
        subtitle="Specify event location details and guest capacity"
        onBack={onBack} 
      />

      <form onSubmit={handleSubmit} className="space-y-8 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          <div className="space-y-6">
            {/* Venue Name */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Venue Name</label>
              <div className="relative">
                <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="text" required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})} 
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  placeholder="e.g. Grand Ballroom"
                />
              </div>
            </div>

            {/* Location */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Location / Floor</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="text" required
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  placeholder="e.g. 5th Floor, West Wing"
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {/* Capacity */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Capacity (Pax)</label>
                <div className="relative">
                  <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                  <input 
                    type="number" required
                    value={formData.capacity}
                    onChange={(e) => setFormData({...formData, capacity: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                    placeholder="500"
                  />
                </div>
              </div>

              {/* Status */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Status</label>
                <select 
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                >
                  <option value="Active">Active</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Full">Fully Booked</option>
                </select>
              </div>
            </div>

            {/* Facilities Summary */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Facilities Highlights</label>
              <div className="relative">
                <Info className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="text"
                  value={formData.facilities}
                  onChange={(e) => setFormData({...formData, facilities: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all text-slate-800 dark:text-white"
                  placeholder="e.g. WiFi, Projector, Sound System"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="md:col-span-2 flex flex-col gap-1.5">
            <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Venue Description</label>
            <div className="relative">
              <AlignLeft className="absolute left-4 top-4 text-stone-400" size={14} />
              <textarea 
                rows="4"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full pl-10 pr-4 py-4 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none resize-none leading-relaxed focus:border-blue-500 text-slate-800 dark:text-white"
                placeholder="Briefly describe the venue accessibility or special features..."
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-6 border-t border-stone-50 dark:border-white/5 flex justify-end gap-3">
          <button 
            type="button" 
            onClick={onBack}
            className="px-8 py-3 rounded-xl text-sm font-semibold text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-white/5 transition-all"
          >
            Discard
          </button>
          <button 
            type="submit"
            className="px-10 py-3 bg-[#0F172A] dark:bg-blue-600 text-white rounded-xl text-sm font-semibold shadow-lg hover:bg-slate-800 dark:hover:bg-blue-700 transition-all flex items-center gap-2"
          >
            <Save size={18} />
            {initialData ? "Update Venue" : "Save Venue"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormVenue;